import { find, minBy } from 'lodash-es';

const customPackaging_coefficients = [
  { volume: 0, coefficient: 1.5 },
  { volume: 1100, coefficient: 1.3 },
  { volume: 1200, coefficient: 1.1 },
  { volume: 1500, coefficient: 1 },
  { volume: 1700, coefficient: 0.9 },
  { volume: 2000, coefficient: 0.7 },
  { volume: 2600, coefficient: 0.6 },
  { volume: 3000, coefficient: 0.5 },
  { volume: 4000, coefficient: 0.4 },
  { volume: 10000, coefficient: 0.4 },
];

const findClosestCoefficient = (data, key, target) => {
  return data.reduce((acc, obj) => (Math.abs(target - obj[key]) < Math.abs(target - acc[key]) && obj[key] < target ? obj : acc));
};

export const findCompatibleOptionsByDimensions = ({ data, dimensions, product_type }) => {
  let longSide = Math.max(dimensions.width, dimensions.length);
  let shortSide = Math.min(dimensions.width, dimensions.length);

  // Match Organizers and Medical Organizers to the same product type, return the product type in neither
  let processedProductType = () => {
    if (product_type === 'Organizers' || product_type === 'Medical Organizers') {
      return 'Organizers';
    } else {
      return product_type;
    }
  };

  return data.filter((box) => {
    return (
      (box.width >= longSide || box.length >= longSide) &&
      (box.length >= shortSide || box.length >= shortSide) &&
      box.height >= dimensions.height &&
      box.product_type.name === processedProductType()
    );
  });
};

const getFreightAndPackagingCost = ({ packaging, width, length, height, specs }) => {
  let productType = specs.product_type?.name;

  let packagingCandidates = findCompatibleOptionsByDimensions({
    data: packaging,
    dimensions: { width, length, height },
    product_type: productType,
  });

  let shippingPackage = minBy(packagingCandidates, (o) => {
    return o.box_cost + o.accessories_cost + o.labor_cost;
  });

  var packagingCost;
  var oversizedPerCubicCost = 0.0114264;
  var oversizedLaborCost = 4.761;

  let freightCostPerVolume = 436.168;
  var freightCost;

  switch (productType) {
    case 'Caddies':
      freightCost = width * length * height * 0.0000163871 * freightCostPerVolume;
      let caddyVolume = width * length * height;
      let caddyVolumeCf = findClosestCoefficient(customPackaging_coefficients, 'volume', caddyVolume);
      packagingCost = caddyVolumeCf.coefficient * caddyVolume * 0.0114264 + 4.761;
      break;

    default:
      if (Boolean(shippingPackage)) {
        freightCost = shippingPackage.volume * freightCostPerVolume;
        // console.log({ shippingPackage, freightCost });
        packagingCost = shippingPackage?.box_cost + shippingPackage?.accessories_cost + shippingPackage?.labor_cost;
      } else {
        let volume = length * width * height;
        freightCost = volume * 0.00714753;
        let oversizedCoefficient = findClosestCoefficient(customPackaging_coefficients, 'volume', volume);
        // console.log({ volume, oversizedPerCubicCost, oversizedLaborCost, oversizedCoefficient });
        let oversizedCost = volume * oversizedPerCubicCost * oversizedCoefficient?.coefficient + oversizedLaborCost;
        packagingCost = oversizedCost;
      }
      break;
  }
  return { freightCost, packagingCost };
};

export const getSpiceRackCost = ({ length, width, height, specs, baseUnit, freight, packaging, retail }) => {
  if (baseUnit === 'metric') {
    width = width / 25.4;
    height = height / 25.4;
    length = length / 25.4;
  }

  let acrylicMultiplier = 0.01185;
  let excess = 0.7;
  let loss = 0.08;
  let freightCostPerM3 = 436.168;

  let base = width * length * (1 + loss);
  let top = width * (length + excess) * (1 + loss);
  let sides = width * height * (1 + loss) * 2;
  let baseCost = base + top + sides;

  let acrylicCost = baseCost * acrylicMultiplier;
  let assemblyCost = acrylicCost;

  const { freightCost, packagingCost } = getFreightAndPackagingCost({ freight, packaging, width, length, height, specs });

  if (retail) {
    let total = acrylicCost + assemblyCost + packagingCost + freightCost;
    let multiplier = 3;
    return Math.round(total * multiplier * 100);
  } else {
    let total = acrylicCost + assemblyCost + packagingCost;
    return Math.round(total * 100);
  }
};

const findSizeConfig = (specs, width) => {
  if (!specs.sizes?.length) return null;
  
  // Sort sizes by min width to ensure proper order
  const sortedSizes = [...specs.sizes].sort((a, b) => 
    parseFloat(a.width.min) - parseFloat(b.width.min)
  );
  
  return sortedSizes.find((size, index) => {
    const currentMinWidth = parseFloat(size.width.min);
    const nextMinWidth = index < sortedSizes.length - 1 
      ? parseFloat(sortedSizes[index + 1].width.min)
      : Infinity;
    
    return width >= currentMinWidth && width < nextMinWidth;
  });
};

const getSizeLabel = (specs, width) => {
  const sizeConfig = findSizeConfig(specs, width);
  return sizeConfig?.label ?? 'Custom';
};

export const getProductName = (specs, width) => {
  const baseName = specs.name || '';
  const sizeLabel = getSizeLabel(specs, width);
  const sizePart = sizeLabel !== 'Custom' ? ` - ${sizeLabel}` : '';
  
  return `${baseName}${sizePart} (Custom Order)`;
};

const findOrganizerSpecs = (specs, width) => {
  // Find the matching size configuration
  const sizeConfig = findSizeConfig(specs, width);
  
  // Destructure values from sizeConfig, falling back to specs or defaults
  const {
    label = 'Custom',
    length_lines = 0,
    width_lines = 0,
    parts = 0,
    machining = 0,
    complexity = 0
  } = sizeConfig ?? specs;

  // Constants for cost calculations
  const costParams = {
    multiplier: 4.75,
    acrylicMultiplier: 0.01185,
    machiningMultiplier: 0.69,
    baseLoss: 0.04,
    sidesLoss: 0.08,
    wMidLoss: 0.09,
    lMidLoss: 0.09
  };

  return {
    ...specs,
    ...costParams,
    label,
    productName: getProductName(specs, width),
    length_lines,
    width_lines,
    parts,
    machining,
    complexity
  };
};

export const getOrganizerCost = ({ length, width, height, specs: baseSpecs, baseUnit, freight, packaging, retail }) => {
  let specs = findOrganizerSpecs(baseSpecs, width);

  if (baseUnit === 'metric') {
    width = width / 25.4;
    height = height / 25.4;
    length = length / 25.4;
  } else {
    width = Math.round(width * 10) / 10;
    height = Math.round(height * 10) / 10;
    length = Math.round(length * 10) / 10;
  }

  let length_lines = Number(specs.length_lines) || 0;
  let width_lines = Number(specs.width_lines) || 0;

  // 2023-05-01 - https://app.asana.com/0/1200856215030468/1204394413482175/f
  // let productType = specs.product_type?.name;
  // let multiplier = productType == 'Medical Organizers' ? 4.75 : 4.25;
  let multiplier = 2;

  let acrylicCost = 0.01185;
  let acrylicMultiplier = 2.28;
  let machiningMultiplier = 0.69;
  let assemblyMultiplier = 4.43;
  let baseLoss = 0.04;
  let sidesLoss = 0.08;
  let wMidLoss = 0.09;
  let lMidLoss = 0.09;

  let base = length * width * (1 + baseLoss);
  let sides = (length * height * 2 + width * height * 2) * (1 + sidesLoss);
  let width_mid = width_lines * width * height * (1 + wMidLoss);
  let length_mid = length_lines * length * height * (1 + lMidLoss);
  let total = base + sides + length_mid + width_mid;
  let acrylic = total * acrylicCost * acrylicMultiplier;

  let machiningFactor = Boolean(specs.machining) ? specs.machining : 0;
  let assembly = specs.parts * (1 - machiningFactor) * specs.complexity * machiningMultiplier * assemblyMultiplier;

  let packagingMultiplier1 = 0.0078;
  let packagingMultiplier2 = 12.79;
  let packagingCost = 2 * (width * length + width * height + length * height) * packagingMultiplier1 + packagingMultiplier2;
  let freightMultiplier = 40;
  let freightCost = ((width * length * height) / 1300) * freightMultiplier;

  if (retail) {
    // Calculate retail cost for the consumer
    let cost = acrylic + assembly + packagingCost + freightCost;
    let retailCost = cost * multiplier;
    // console.table({ base, sides, width_mid, length_mid, total, acrylic, assembly, freightCost, packagingCost, cost });
    return Math.round(retailCost * 100);
  } else {
    // Calculate factory cost
    let cost = acrylic + assembly + packagingCost;
    // console.log('Factory Cost:', new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cost));
    return Math.round(cost * 100);
  }
};

export const getCaddyCost = ({ length, width, height, specs, baseUnit, freight, packaging, retail }) => {
  if (baseUnit === 'metric') {
    width = width / 25.4;
    height = height / 25.4;
    length = length / 25.4;
  }

  let length_lines = Number(specs.length_lines) || 0;
  let width_lines = Number(specs.width_lines) || 0;

  let multiplier = 3;

  let acrylicMultiplier = 0.01185;
  let machiningMultiplier = 1.38;
  let baseLoss = 0.04;
  let sidesLoss = 0.08;
  let wMidLoss = 0.09;
  let lMidLoss = 0.09;

  let base = length * width * (1 + baseLoss) * 2;
  let sides = (length * height * 2 + width * height * 2) * (1 + sidesLoss);
  let width_mid = width_lines * width * height * (1 + wMidLoss);
  let length_mid = length_lines * length * height * (1 + lMidLoss);
  let total = base + sides + length_mid + width_mid;
  let acrylic = total * acrylicMultiplier;
  let machiningFactor = specs.machining || 0;
  let assembly = specs.parts * (1 - machiningFactor) * specs.complexity * machiningMultiplier;

  const { freightCost, packagingCost } = getFreightAndPackagingCost({ freight, packaging, width, length, height, specs });

  if (retail) {
    let landedCost = acrylic + assembly + packagingCost + freightCost;
    let cost = landedCost * multiplier;
    // console.log({ base, sides, width_mid, length_mid, total, acrylic, assembly, freightCost, packagingCost, landedCost, cost });
    return Math.round(cost * 100);
  } else {
    let landedCost = acrylic + assembly + packagingCost;
    return Math.round(landedCost * 100);
  }
};
